// Servicio centralizado para operaciones de chat con Supabase.
// Maneja todas las llamadas a la base de datos relacionadas con chats.
// Usa funciones RPC y consultas directas a tablas.
// Adaptado para web desde amarsi-app.

import { supabase } from '../lib/supabase';
import { cache } from '../utils/cache';
import { withNetworkRetry } from '../utils/retry';
import { logger } from '../utils/logger';
import { chatValidations } from '../utils/validations';

// Logger específico para el módulo de chat
const chatLogger = logger.createModuleLogger('chatService');

export const chatService = {
  // Obtiene los chatrooms del usuario actual.
  // Ahora con cache inteligente y validaciones mejoradas.
  // Valida sesión antes de procesar la solicitud.
  // Retorna lista de chatrooms con información de amigos y últimos mensajes.
  async getMyChatrooms(session) {
    // Validar parámetros de entrada
    const validation = chatValidations.getMyChatrooms(session);
    if (!validation.isValid) {
      const error = new Error(`Validación fallida: ${validation.errors.join(', ')}`);
      chatLogger.error('getMyChatrooms', error, { 
        phase: 'validation_error',
        errors: validation.errors 
      });
      throw error;
    }
    
    const cacheKey = 'my_chatrooms';
    
    // Intentar obtener del cache primero
    const cached = cache.get(cacheKey);
    if (cached) {
      chatLogger.info('getMyChatrooms', { source: 'cache', count: cached.length });
      return cached;
    }
    
    chatLogger.info('getMyChatrooms', { phase: 'cache_miss', action: 'calling_rpc' });
    
    // Usar sistema de reintentos para recuperación automática de fallos
    const { data, error } = await withNetworkRetry(async () => {
      return await supabase.rpc('get_my_chatrooms');
    }, {
      onRetry: ({ attempt, maxTries, delay, error }) => {
        chatLogger.warn('getMyChatrooms', { 
          attempt, 
          maxTries, 
          delay, 
          error: error.message,
          phase: 'rpc_retry'
        });
      }
    });
    
    chatLogger.debug('getMyChatrooms', { 
      phase: 'rpc_response', 
      hasData: !!data, 
      hasError: !!error,
      dataLength: data?.length
    });
    
    if (error) {
      chatLogger.error('getMyChatrooms', error, { phase: 'rpc_error' });
      throw error;
    }
    
    // Si no hay datos o es null, retornar array vacío
    if (!data || data === null) {
      chatLogger.info('getMyChatrooms', { phase: 'no_data', action: 'return_empty' });
      const emptyResult = [];
      cache.set(cacheKey, emptyResult, 2 * 60 * 1000); // Cache por 2 min para resultados vacíos
      return emptyResult;
    }
    
    chatLogger.info('getMyChatrooms', { 
      phase: 'success', 
      count: data.length,
      action: 'cache_and_return'
    });
    
    // Guardar en cache con TTL de 1 minuto para datos válidos (más corto para mayor frescura)
    cache.set(cacheKey, data, 1 * 60 * 1000);
    
    return data;
  },

  // Obtiene los mensajes de un chatroom específico.
  // Ahora con cache inteligente y validaciones mejoradas.
  // Valida friendId antes de procesar la solicitud.
  // TTL más corto para mensajes (2 min) ya que cambian frecuentemente.
  // Parámetros: friendId (ID del amigo), session (sesión actual).
  // Retorna lista de mensajes agrupados por fecha.
  async getChatroomMessages(friendId, session = null) {
    // Validar parámetros de entrada
    const validation = chatValidations.getChatroomMessages(friendId);
    if (!validation.isValid) {
      const error = new Error(`Validación fallida: ${validation.errors.join(', ')}`);
      chatLogger.error('getChatroomMessages', error, { 
        friendId,
        phase: 'validation_error',
        errors: validation.errors 
      });
      throw error;
    }
    
    const cacheKey = `chatroom_messages:${friendId}`;
    
    // Intentar obtener del cache primero (TTL más corto para mensajes)
    const cached = cache.get(cacheKey);
    if (cached) {
      chatLogger.info('getChatroomMessages', { 
        friendId, 
        source: 'cache', 
        groupsCount: cached.length 
      });
      return cached;
    }
    
    chatLogger.info('getChatroomMessages', { 
      friendId, 
      phase: 'cache_miss', 
      action: 'fetch_from_db'
    });
    
    // Método 1: Intentar con RPC con reintentos
    try {
      chatLogger.debug('getChatroomMessages', { 
        friendId, 
        method: 'rpc', 
        action: 'attempting_call'
      });
      
      const informacion = { friend_id_arg: friendId };
      
      const { data, error } = await withNetworkRetry(async () => {
        return await supabase.rpc('get_chatroom_messages_alt', informacion);
      }, {
        onRetry: ({ attempt, maxTries, delay, error }) => {
          chatLogger.warn('getChatroomMessages', { 
            friendId,
            attempt, 
            maxTries, 
            delay, 
            error: error.message,
            phase: 'rpc_retry'
          });
        }
      });
      
      chatLogger.debug('getChatroomMessages', { 
        friendId,
        phase: 'rpc_response', 
        hasData: !!data, 
        hasError: !!error,
        dataType: typeof data,
        dataLength: data?.length
      });
      
      if (!error && data) {
        if (Array.isArray(data)) {
          chatLogger.info('getChatroomMessages', { 
            friendId,
            method: 'rpc',
            phase: 'success',
            groupsCount: data.length,
            action: 'processing_groups'
          });
          
          data.forEach((grupo, index) => {
            chatLogger.debug('getChatroomMessages', { 
              friendId,
              groupIndex: index,
              dateTrunc: grupo.date_trunc,
              mensajesCount: grupo.mensajes_list?.length || 0,
              firstMessageId: grupo.mensajes_list?.[0]?.id
            });
          });
        }
        // Guardar en cache antes de retornar
        cache.set(cacheKey, data, 2 * 60 * 1000); // 2 minutos para mensajes
        return data;
      } else if (error) {
        chatLogger.warn('getChatroomMessages', { 
          friendId,
          method: 'rpc',
          phase: 'failed',
          error: error.message,
          action: 'fallback_to_direct'
        });
      }
    } catch (rpcError) {
      chatLogger.warn('getChatroomMessages', { 
        friendId,
        method: 'rpc',
        phase: 'exception',
        error: rpcError.message,
        action: 'fallback_to_direct'
      });
    }
    
    // Método 2: Fallback a consulta directa
    try {
      chatLogger.info('getChatroomMessages', { 
        friendId,
        method: 'direct_query',
        phase: 'starting_fallback'
      });
      
      // Obtener chatroom entre los dos usuarios
      const userId = session?.user?.id;
      if (!userId) {
        chatLogger.warn('getChatroomMessages', { 
          friendId,
          phase: 'no_session',
          action: 'return_empty'
        });
        const emptyResult = [];
        cache.set(cacheKey, emptyResult, 1 * 60 * 1000); // 1 minuto para sin sesión
        return emptyResult;
      }
      
      const { data: chatroom } = await withNetworkRetry(async () => {
        return await supabase
          .from('chatrooms')
          .select('id')
          .or(`and(remitente.eq.${userId},destinatario.eq.${friendId}),and(remitente.eq.${friendId},destinatario.eq.${userId})`)
          .single();
      }, {
        onRetry: ({ attempt, maxTries, delay, error }) => {
          chatLogger.warn('getChatroomMessages', { 
            friendId,
            attempt, 
            maxTries, 
            delay, 
            error: error.message,
            phase: 'direct_query_retry'
          });
        }
      });
      
      if (!chatroom) {
        chatLogger.info('getChatroomMessages', { 
          friendId,
          phase: 'no_chatroom',
          action: 'return_empty'
        });
        const emptyResult = [];
        cache.set(cacheKey, emptyResult, 1 * 60 * 1000); // 1 minuto para resultados vacíos
        return emptyResult;
      }
      
      chatLogger.debug('getChatroomMessages', { 
        friendId,
        chatroomId: chatroom.id,
        phase: 'chatroom_found'
      });
      
      // Obtener mensajes del chatroom con reintentos
      const { data: messages, error: messagesError } = await withNetworkRetry(async () => {
        return await supabase
          .from('mensajitos')
          .select('*')
          .eq('chatroom_id', chatroom.id)
          .order('created_at', { ascending: false });
      }, {
        onRetry: ({ attempt, maxTries, delay, error }) => {
          chatLogger.warn('getChatroomMessages', { 
            friendId,
            attempt, 
            maxTries, 
            delay, 
            error: error.message,
            phase: 'messages_query_retry'
          });
        }
      });
      
      if (messagesError) {
        chatLogger.error('getChatroomMessages', messagesError, { 
          friendId,
          phase: 'messages_query_error'
        });
        throw messagesError;
      }
      
      chatLogger.debug('getChatroomMessages', { 
        friendId,
        messagesCount: messages?.length,
        phase: 'messages_obtained'
      });
      
      // Agrupar mensajes por fecha (similar a la estructura de la RPC)
      const groupedMessages = this.groupMessagesByDate(messages || []);
      
      chatLogger.info('getChatroomMessages', { 
        friendId,
        originalMessagesCount: messages?.length,
        groupedMessagesCount: groupedMessages.length,
        phase: 'messages_grouped',
        action: 'cache_and_return'
      });
      
      // Guardar en cache antes de retornar
      cache.set(cacheKey, groupedMessages, 2 * 60 * 1000); // 2 minutos para mensajes
      
      return groupedMessages;
      
    } catch (directError) {
      chatLogger.error('getChatroomMessages', directError, { 
        friendId,
        phase: 'direct_query_error',
        originalError: directError.message
      });
      throw new Error(`No se pudieron obtener mensajes: ${directError.message}`);
    }
  },

  // Función auxiliar para agrupar mensajes por fecha
  // Usa la hora local del dispositivo para agrupar correctamente
  groupMessagesByDate(messages) {
    const grouped = {};
    
    messages.forEach(message => {
      const date = new Date(message.created_at);
      // Usar métodos locales para obtener fecha en zona horaria del dispositivo
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateTrunc = `${year}-${month}-${day}`; // YYYY-MM-DD en hora local
      
      if (!grouped[dateTrunc]) {
        grouped[dateTrunc] = {
          date_trunc: dateTrunc,
          mensajes_list: []
        };
      }
      
      grouped[dateTrunc].mensajes_list.push(message);
    });
    
    // Convertir a array y ordenar por fecha (más antigua primero, más reciente al final - como WhatsApp)
    return Object.values(grouped).sort((a, b) => 
      new Date(a.date_trunc) - new Date(b.date_trunc)
    );
  },

  // Envía un mensaje usando la función RPC 'despacho_de_mensaje'.
  // Ahora con validaciones mejoradas y cache inteligente.
  // Valida el objeto mensaje antes de procesar.
  // Parámetros: objeto mensaje con chatroom_id, body, destinatario, msg_type, etc.
  // Maneja respuestas y actualiza interacciones.
  async sendMessage(mensajito) {
    const DEBUG = process.env.NODE_ENV === 'development';
    
    if (DEBUG) {
      console.log('=== chatService.sendMessage: INICIANDO ENVÍO ===');
      console.log('chatService.sendMessage: Mensaje a enviar:', mensajito);
      console.log('chatService.sendMessage: Timestamp:', new Date().toISOString());
    }
    
    // Validar parámetros de entrada
    const validation = chatValidations.sendMessage(mensajito);
    if (!validation.isValid) {
      const error = new Error(`Validación fallida: ${validation.errors.join(', ')}`);
      if (DEBUG) {
        console.log('❌ chatService.sendMessage: ERROR DE VALIDACIÓN');
        console.log('chatService.sendMessage: Errores:', validation.errors);
        console.log('chatService.sendMessage: Mensaje:', mensajito);
      }
      chatLogger.error('sendMessage', error, { 
        destinatario: mensajito?.destinatario,
        phase: 'validation_error',
        errors: validation.errors 
      });
      throw error;
    }
    
    if (DEBUG) {
      console.log('✅ chatService.sendMessage: Validación exitosa');
      console.log('chatService.sendMessage: Ejecutando despacho_de_mensaje RPC');
    }
    
    // Usar sistema de reintentos para envío de mensaje
    if (DEBUG) {
      console.log('chatService.sendMessage: Iniciando ejecución con reintentos');
    }
    
    // Transformar objeto mensajito en parámetros individuales para despacho_de_mensaje_v2
    // Esto evita problemas con Supabase REST API y funciones JSONB
    const rpcParams = {
      p_destinatario: mensajito.destinatario,
      p_body: mensajito.body,
      p_msg_type: mensajito.msg_type || 1,
      p_id_ref: mensajito.id_ref || null,
      p_msg_extra_data: mensajito.msg_extra_data || {}
    };
    
    const { data, error } = await withNetworkRetry(async () => {
      if (DEBUG) {
        console.log('chatService.sendMessage: Ejecutando RPC despacho_de_mensaje_v2');
        console.log('chatService.sendMessage: Parámetros:', rpcParams);
      }
      
      const result = await supabase.rpc('despacho_de_mensaje_v2', rpcParams);
      
      if (DEBUG) {
        console.log('chatService.sendMessage: Resultado RPC:', result);
        console.log('chatService.sendMessage: Data:', result.data);
        console.log('chatService.sendMessage: Error:', result.error);
      }
      
      return result;
    }, {
      onRetry: ({ attempt, maxTries, delay, error }) => {
        if (DEBUG) {
          console.log('⏰ chatService.sendMessage: REINTENTO', attempt, 'de', maxTries);
          console.log('chatService.sendMessage: Delay:', delay, 'ms');
          console.log('chatService.sendMessage: Error:', error.message);
        }
        chatLogger.warn('sendMessage', { 
          destinatario: mensajito.destinatario,
          attempt, 
          maxTries, 
          delay, 
          error: error.message,
          phase: 'message_dispatch_retry'
        });
      }
    });
    
    if (DEBUG) {
      console.log('chatService.sendMessage: Resultado final de withNetworkRetry:');
      console.log('  - data:', data);
      console.log('  - error:', error);
      console.log('  - data type:', typeof data);
      console.log('  - error type:', typeof error);
    }
    
    if (error) {
      if (DEBUG) {
        console.log('❌ chatService.sendMessage: ERROR EN ENVÍO DE MENSAJE');
        console.log('chatService.sendMessage: Error:', error);
        console.log('chatService.sendMessage: Error message:', error.message);
        console.log('chatService.sendMessage: Error details:', error.details);
        console.log('chatService.sendMessage: Destinatario:', mensajito.destinatario);
      }
      chatLogger.error('sendMessage', error, { 
        destinatario: mensajito.destinatario,
        phase: 'message_dispatch_error'
      });
      throw error;
    }
    
    if (DEBUG) {
      console.log('✅ chatService.sendMessage: MENSAJE ENVIADO EXITOSAMENTE');
      console.log('chatService.sendMessage: Data recibida:', data);
      console.log('chatService.sendMessage: Actualizando interacciones...');
    }

    // Actualiza interacciones (tipo 3: mensaje enviado) con reintentos
    if (DEBUG) {
      console.log('chatService.sendMessage: Actualizando interacciones (tipo 3)');
      console.log('chatService.sendMessage: Parámetros interactions_manager_alt:', {
        friend_id: mensajito.destinatario,
        tipo: 3,
        ahorita: new Date().toISOString(),
      });
    }
    
    const { error: interactionError } = await withNetworkRetry(async () => {
      if (DEBUG) {
        console.log('chatService.sendMessage: Ejecutando interactions_manager_alt RPC');
      }
      
      const result = await supabase.rpc('interactions_manager_alt', {
        friend_id: mensajito.destinatario,
        tipo: 3,
        ahorita: new Date().toISOString(),
      });
      
      if (DEBUG) {
        console.log('chatService.sendMessage: Resultado interactions_manager_alt:', result);
      }
      
      return result;
    }, {
      onRetry: ({ attempt, maxTries, delay, error }) => {
        if (DEBUG) {
          console.log('⏰ chatService.sendMessage: REINTENTO INTERACCIONES', attempt, 'de', maxTries);
          console.log('chatService.sendMessage: Error interacciones:', error.message);
        }
        chatLogger.warn('sendMessage', { 
          destinatario: mensajito.destinatario,
          attempt, 
          maxTries, 
          delay, 
          error: error.message,
          phase: 'interaction_update_retry'
        });
      }
    });
    
    if (interactionError) {
      if (DEBUG) {
        console.log('❌ chatService.sendMessage: ERROR EN ACTUALIZACIÓN DE INTERACCIONES');
        console.log('chatService.sendMessage: Interaction error:', interactionError);
      }
      chatLogger.error('sendMessage', interactionError, { 
        destinatario: mensajito.destinatario,
        phase: 'interaction_update_error'
      });
    } else {
      if (DEBUG) {
        console.log('✅ chatService.sendMessage: INTERACCIONES ACTUALIZADAS EXITOSAMENTE');
      }
    }

    // Invalidar cache relevante después de enviar mensaje
    if (DEBUG) {
      console.log('chatService.sendMessage: Invalidando cache relevante...');
    }
    chatLogger.info('sendMessage', { 
      destinatario: mensajito.destinatario,
      phase: 'cache_invalidation',
      action: 'invalidating_all_chat_cache'
    });
    
    if (DEBUG) {
      console.log('=== chatService.sendMessage: ENVÍO COMPLETADO ===');
      console.log('chatService.sendMessage: Timestamp:', new Date().toISOString());
    }
    
    // Invalidar TODAS las claves relacionadas para asegurar actualización
    cache.remove('my_chatrooms'); // Invalidar lista de chatrooms
    cache.remove(`chatroom_messages:${mensajito.destinatario}`); // Cache del chat actual
    
    // También invalidar cache del remitente (para la vista del otro usuario)
    cache.remove(`chatroom_messages:${mensajito.remitente || 'current'}`);
    
    // Forzar invalidación específica en React Query (solo chat actual y lista de chatrooms)
    if (typeof window !== 'undefined' && window.__REACT_QUERY_CLIENT__) {
      window.__REACT_QUERY_CLIENT__.invalidateQueries(['chatrooms']);
    }
    
    return data;
  },

  // Marca un mensaje como leído.
  // Actualiza la tabla 'mensajitos' directamente.
  // Parámetros: messageId (ID del mensaje).
  async markMessageAsRead(messageId) {
    const { data, error } = await supabase
      .from('mensajitos')
      .update({ is_leido: true })
      .eq('id', messageId)
      .select();
    if (error) throw error;
    return data;
  },

  // Elimina un chatroom de forma suave.
  // Llama a la función RPC 'delete_soft_chatroom'.
  // Parámetros: session y chatroom_id.
  async deleteChatroom(session, chatroomId) {
    const parametros = { session, chatroom_id: chatroomId };
    const { data, error } = await supabase.rpc('delete_soft_chatroom', parametros);
    if (error) throw error;
    return data;
  },

  // Obtiene mensajes de chatroom con paginación.
  // Implementa paginación para manejar grandes volúmenes de mensajes eficientemente.
  // Parámetros: friendId, page (offset), pageSize, session.
  // Retorna: mensajes de la página, si hay más páginas, y total de mensajes.
  async getChatroomMessagesPaginated(friendId, page = 0, pageSize = 50, session = null) {
    console.log(`chatService.getChatroomMessagesPaginated: friendId=${friendId}, page=${page}, pageSize=${pageSize}`);
    
    try {
      // Validar parámetros
      if (!friendId || typeof friendId !== 'string') {
        console.error('chatService.getChatroomMessagesPaginated: friendId inválido:', friendId);
        throw new Error('friendId inválido');
      }
      
      if (page < 0) {
        console.error('chatService.getChatroomMessagesPaginated: page debe ser >= 0:', page);
        throw new Error('page debe ser >= 0');
      }
      
      if (pageSize <= 0 || pageSize > 100) {
        console.error('chatService.getChatroomMessagesPaginated: pageSize inválido:', pageSize);
        throw new Error('pageSize debe estar entre 1 y 100');
      }

      const userId = session?.user?.id;
      if (!userId) {
        console.log('chatService.getChatroomMessagesPaginated: No hay sesión activa');
        return {
          messages: [],
          hasMore: false,
          totalCount: 0,
          page: page
        };
      }

      // Intentar con función RPC paginada primero
      try {
        const informacion = { 
          friend_id_arg: friendId,
          page_offset: page * pageSize,
          page_limit: pageSize
        };
        
        console.log('chatService.getChatroomMessagesPaginated: Parámetros RPC:', informacion);
        
        const { data, error } = await supabase.rpc('get_chatroom_messages_paginated', informacion);
        
        if (!error && data) {
          console.log('chatService.getChatroomMessagesPaginated: RPC exitoso:', {
            messagesCount: data.messages?.length || 0,
            hasMore: data.hasMore,
            totalCount: data.totalCount
          });
          
          return {
            messages: data.messages || [],
            hasMore: data.hasMore || false,
            totalCount: data.totalCount || 0,
            page: page
          };
        }
      } catch (rpcError) {
        console.warn('chatService.getChatroomMessagesPaginated: RPC falló, usando fallback:', rpcError);
      }

      // Fallback a consulta directa con paginación
      console.log('chatService.getChatroomMessagesPaginated: Usando consulta directa con paginación');
      
      // Obtener chatroom entre los dos usuarios
      const { data: chatroom } = await supabase
        .from('chatrooms')
        .select('id')
        .or(`and(remitente.eq.${userId},destinatario.eq.${friendId}),and(remitente.eq.${friendId},destinatario.eq.${userId})`)
        .single();
      
      if (!chatroom) {
        console.log('chatService.getChatroomMessagesPaginated: No existe chatroom, retornando vacío');
        return {
          messages: [],
          hasMore: false,
          totalCount: 0,
          page: page
        };
      }
      
      console.log('chatService.getChatroomMessagesPaginated: Chatroom encontrado:', chatroom.id);
      
      // Obtener mensajes paginados del chatroom
      const { data: messages, error: messagesError } = await supabase
        .from('mensajitos')
        .select('*')
        .eq('chatroom_id', chatroom.id)
        .order('created_at', { ascending: false })
        .range(page * pageSize, (page + 1) * pageSize - 1); // Paginación manual
      
      if (messagesError) {
        console.error('chatService.getChatroomMessagesPaginated: Error en consulta paginada:', messagesError);
        throw messagesError;
      }
      
      console.log('chatService.getChatroomMessagesPaginated: Mensajes obtenidos:', messages?.length);
      
      // Obtener conteo total para determinar si hay más páginas
      const { count, error: countError } = await supabase
        .from('mensajitos')
        .select('*', { count: 'exact', head: false })
        .eq('chatroom_id', chatroom.id);
      
      if (countError) {
        console.warn('chatService.getChatroomMessagesPaginated: Error obteniendo conteo:', countError);
      }
      
      const totalCount = count || 0;
      const offset = page * pageSize;
      const hasMore = offset + messages.length < totalCount;
      
      console.log('chatService.getChatroomMessagesPaginated: Resultado paginación directa:', {
        messagesCount: messages.length,
        totalCount,
        hasMore,
        offset,
        page
      });
      
      return {
        messages: messages || [],
        hasMore,
        totalCount,
        page
      };
      
    } catch (error) {
      console.error('chatService.getChatroomMessagesPaginated: Error general:', error);
      throw new Error(`No se pudieron obtener mensajes paginados: ${error.message}`);
    }
  },

  // Obtiene o crea el ID del chatroom entre dos usuarios.
  // Llama a la función RPC 'check_chatroom_exist'.
  // Parámetros: userId y friendId.
  async getOrCreateChatroomId(userId, friendId) {
    const { data, error } = await supabase.rpc('check_chatroom_exist', {
      current_id: userId,
      friend_id: friendId
    });
    if (error) throw error;
    return data?.[0]?.chatroom_id;
  },

  // Envía imagen persistente (acceso ilimitado, msg_type 3).
  // Inserta en shared_images con access_type 'unlimited' y luego en mensajitos.
  // Parámetros: chatroom_id, destinatario, image_id, owner_id.
  async sendPersistentImage({ chatroom_id, destinatario, image_id, owner_id }) {
    console.log('chatService.sendPersistentImage: Enviando imagen persistente, image_id:', image_id);
    
    try {
      // Insertar en shared_images con acceso ilimitado
      const { data: sharedImage, error: sharedError } = await supabase
        .from('shared_images')
        .insert({
          share_id: this.generateUUID(),
          image_id: image_id,
          owner_id: owner_id,
          access_type: 'unlimited',
          is_viewed: false,
          is_active: true
        })
        .select()
        .single();

      if (sharedError) {
        console.error('chatService.sendPersistentImage: Error insertando en shared_images:', sharedError);
        throw sharedError;
      }

      console.log('chatService.sendPersistentImage: shared_image creado:', sharedImage.id);

      // Insertar mensaje en mensajitos con msg_type 3
      const mensajito = {
        ahorita: new Date(),
        chatroom_id: chatroom_id,
        body: 'Shared Image',
        destinatario: destinatario,
        msg_type: 3,
        shared_image_id: sharedImage.id
      };

      const result = await this.sendMessage(mensajito);
      console.log('chatService.sendPersistentImage: Mensaje enviado exitosamente');
      return result;

    } catch (error) {
      console.error('chatService.sendPersistentImage: Error al enviar imagen persistente:', error);
      throw error;
    }
  },

  // Envía imagen efímera (view once, msg_type 6).
  // Inserta en shared_images con access_type 'view_once' y luego en mensajitos.
  // Parámetros: chatroom_id, destinatario, image_id, owner_id.
  async sendEphemeralImage({ chatroom_id, destinatario, image_id, owner_id }) {
    console.log('chatService.sendEphemeralImage: Enviando imagen efímera, image_id:', image_id);
    
    try {
      // Insertar en shared_images con acceso view_once
      const { data: sharedImage, error: sharedError } = await supabase
        .from('shared_images')
        .insert({
          share_id: this.generateUUID(),
          image_id: image_id,
          owner_id: owner_id,
          access_type: 'view_once',
          is_viewed: false,
          is_active: true
        })
        .select()
        .single();

      if (sharedError) {
        console.error('chatService.sendEphemeralImage: Error insertando en shared_images:', sharedError);
        throw sharedError;
      }

      console.log('chatService.sendEphemeralImage: shared_image creado:', sharedImage.id);

      // Insertar mensaje en mensajitos con msg_type 6
      const mensajito = {
        ahorita: new Date(),
        chatroom_id: chatroom_id,
        body: 'Imagen temporal',
        destinatario: destinatario,
        msg_type: 6,
        shared_image_id: sharedImage.id
      };

      const result = await this.sendMessage(mensajito);
      console.log('chatService.sendEphemeralImage: Mensaje enviado exitosamente');
      return result;

    } catch (error) {
      console.error('chatService.sendEphemeralImage: Error al enviar imagen efímera:', error);
      throw error;
    }
  },

  // Elimina un mensaje cambiando su msg_type a 0 (mensaje eliminado).
  // Llama a la función RPC 'delete_message'.
  // Solo permite eliminar mensajes propios del usuario.
  // Parámetros: messageId (ID del mensaje).
  async deleteMessage(messageId) {
    console.log('chatService.deleteMessage: Eliminando mensaje:', messageId);
    
    const { data, error } = await supabase.rpc('delete_message', {
      message_id: messageId
    });
    
    if (error) {
      console.error('chatService.deleteMessage: Error al eliminar mensaje:', error);
      throw error;
    }
    
    console.log('chatService.deleteMessage: Mensaje eliminado exitosamente');
    return data;
  },

  // Función helper para reintentar operaciones con backoff
  async _retryWithBackoff(operation, maxRetries = 3, baseDelay = 500) {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        // Verificar si es un error de lock
        const isLockError = error?.message?.includes('Lock') ||
                           error?.message?.includes('lock') ||
                           error?.code === '20' ||
                           error?.details?.includes('Lock');

        if (!isLockError || attempt === maxRetries - 1) {
          throw error;
        }

        // Calcular delay con backoff exponencial
        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`chatService._retryWithBackoff: Reintento ${attempt + 1}/${maxRetries} después de ${delay}ms debido a error de lock`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  },

  // Obtiene la URL de una imagen persistente (msg_type 3).
  // Verifica autorización del usuario antes de retornar la URL.
  // Parámetros: messageId (ID del mensaje).
  async getPersistentImageUrl(messageId) {
    console.log('chatService.getPersistentImageUrl: Obteniendo URL de imagen persistente, messageId:', messageId);

    return this._retryWithBackoff(async () => {
      try {
        // Obtener mensaje con shared_image_id
        const { data: message, error: messageError } = await supabase
          .from('mensajitos')
          .select('shared_image_id, remitente, destinatario')
          .eq('id', messageId)
          .single();

        if (messageError) {
          console.error('chatService.getPersistentImageUrl: Error obteniendo mensaje:', messageError);
          throw messageError;
        }

        if (!message) {
          throw new Error('Mensaje no encontrado');
        }

        // Verificar autorización (el usuario debe ser remitente o destinatario)
        const { data: { user } } = await supabase.auth.getUser();
        if (message.remitente !== user.id && message.destinatario !== user.id) {
          throw new Error('No autorizado para ver esta imagen');
        }

        // Obtener image_id de shared_images
        const { data: sharedImage, error: sharedError } = await supabase
          .from('shared_images')
          .select('image_id')
          .eq('id', message.shared_image_id)
          .single();

        if (sharedError) {
          console.error('chatService.getPersistentImageUrl: Error obteniendo shared_image:', sharedError);
          throw sharedError;
        }

        // Obtener URL de user_images
        const { data: userImage, error: imageError } = await supabase
          .from('user_images')
          .select('url')
          .eq('img_id', sharedImage.image_id)
          .single();

        if (imageError) {
          console.error('chatService.getPersistentImageUrl: Error obteniendo user_image:', imageError);
          throw imageError;
        }

        console.log('chatService.getPersistentImageUrl: URL obtenida exitosamente');
        return userImage.url;

      } catch (error) {
        console.error('chatService.getPersistentImageUrl: Error al obtener URL:', error);
        throw error;
      }
    }, 3, 500);
  },

  // Consume una imagen efímera marcándola como vista.
  // Llama a la función RPC 'consume_view_once'.
  // Parámetros: shareId (ID de la imagen compartida).
  async consumeEphemeralImage(shareId) {
    console.log('chatService.consumeEphemeralImage: Consumiendo imagen efímera, shareId:', shareId);
    
    const { data, error } = await supabase.rpc('consume_view_once', {
      share_id: shareId
    });
    
    if (error) {
      console.error('chatService.consumeEphemeralImage: Error al consumir imagen:', error);
      
      // Manejar códigos de error específicos
      if (error.message === 'NOT_FOUND') {
        throw new Error('Imagen no encontrada');
      } else if (error.message === 'ALREADY_VIEWED') {
        throw new Error('Imagen ya vista');
      } else if (error.message === 'IMAGE_NOT_FOUND') {
        throw new Error('Imagen original no encontrada');
      }
      
      throw error;
    }
    
    console.log('chatService.consumeEphemeralImage: Imagen consumida exitosamente');
    return data;
  },

  // Invalida cache relacionado con un chat específico.
  // Útil para eventos en tiempo real y actualizaciones.
  invalidateChatCache(friendId) {
    console.log(`[CACHE] Invalidando cache para chat con friendId: ${friendId}`);
    
    // Invalidar cache de mensajes del chat específico
    const messagesCacheKey = `chatroom_messages:${friendId}`;
    cache.remove(messagesCacheKey);
    
    // Invalidar lista general de chatrooms
    cache.remove('my_chatrooms');
  },

  // Invalida todo el cache de chat.
  // Útil para logout o cambios globales.
  invalidateAllChatCache() {
    console.log('[CACHE] Invalidando todo el cache de chat');
    
    // Remover todas las keys relacionadas con chat
    const allKeys = cache.getAllKeys();
    const chatKeys = allKeys.filter(key => 
      key.startsWith('chatroom_messages:') || 
      key === 'my_chatrooms'
    );
    
    chatKeys.forEach(key => cache.remove(key));
  },

  // Genera un UUID v4 válido.
  // Se usa para generar share_id en shared_images.
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }
};
